import httpClient from './httpClient';

const getQuotations = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    // Remove undefined values to clean up the request
    ...(filters.quotation_id && { quotation_id: filters.quotation_id }),
    ...(filters.client_name && { client_name: filters.client_name }),
    ...(filters.status && { status: filters.status }),
    ...(filters.price_column && { price_column: filters.price_column }),
    ...(filters.start_date && { start_date: filters.start_date }),
    ...(filters.end_date && { end_date: filters.end_date }),
    // Add sorting parameters
    ...(filters.sort_by && { sort_by: filters.sort_by }),
    ...(filters.sort_order && { sort_order: filters.sort_order }),
    // Add client_type filter for domestic/import filtering
    ...(filters.client_type && { client_type: filters.client_type }),
  };

  try {
    const response = await httpClient.get('/api/quotations', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching quotations:', error);
    throw error;
  }
};

const getQuotation = async (quotationId) => {
  try {
    const response = await httpClient.get(`/api/quotations/${quotationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching quotation:', error);
    throw error;
  }
};

const createQuotation = async (quotationData) => {
  try {
    const response = await httpClient.post('/api/quotations', quotationData);
    return response.data;
  } catch (error) {
    console.error('Error creating quotation:', error);
    throw error;
  }
};

const updateQuotation = async (quotationId, quotationData) => {
  try {
    const response = await httpClient.put(`/api/quotations/${quotationId}`, quotationData);
    return response.data;
  } catch (error) {
    console.error('Error updating quotation:', error);
    throw error;
  }
};

const convertQuotationToOrder = async (quotationId) => {
  try {
    const response = await httpClient.post(`/api/quotations/${quotationId}/convert`, {});
    return response.data;
  } catch (error) {
    console.error('Error converting quotation:', error);
    throw error;
  }
};

/**
 * Partial conversion - creates SO with selected items only.
 * Does NOT delete the original quotation.
 * @param {string} quotationId - Quotation MongoDB ObjectId
 * @param {Array} selectedProducts - Array of {product_index: number, quantity: number}
 * @param {string} notes - Optional notes for the conversion
 * @returns {Promise<Object>} Created sales order
 */
const convertQuotationToOrderPartial = async (quotationId, selectedProducts, notes = '') => {
  try {
    const response = await httpClient.post(`/api/quotations/${quotationId}/convert-partial`, {
      selected_products: selectedProducts,
      notes
    });
    return response.data;
  } catch (error) {
    console.error('Error in partial quotation conversion:', error);
    throw error;
  }
};

/**
 * Get conversion status for a quotation showing per-product converted/remaining quantities.
 * @param {string} quotationId - Quotation MongoDB ObjectId
 * @returns {Promise<Object>} Conversion status with products and history
 */
const getConversionStatus = async (quotationId) => {
  try {
    const response = await httpClient.get(`/api/quotations/${quotationId}/conversion-status`);
    return response.data;
  } catch (error) {
    console.error('Error getting conversion status:', error);
    throw error;
  }
};

const getQuotationStats = async () => {
  try {
    const response = await httpClient.get('/api/quotations/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching quotation stats:', error);
    throw error;
  }
};

const getClientNames = async () => {
  try {
    const response = await httpClient.get('/api/quotations/clients');
    return response.data;
  } catch (error) {
    console.error('Error fetching client names:', error);
    throw error;
  }
};

const deleteQuotation = async (quotationId) => {
  try {
    const response = await httpClient.delete(`/api/quotations/${quotationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting quotation:', error);
    throw error;
  }
};

const reorderItems = async (quotationId, productOrder) => {
  try {
    const response = await httpClient.put(`/api/quotations/${quotationId}/reorder`, {
      product_order: productOrder
    });
    return response.data;
  } catch (error) {
    console.error('Error reordering quotation items:', error);
    throw error;
  }
};

const exportQuotation = async (quotationId, columns = null) => {
  try {
    console.log(`Starting export for quotation ID: ${quotationId}`, columns ? 'with custom columns' : 'with default columns');
    
    const url = `/api/quotations/${quotationId}/export`;
    let response;
    
    if (columns && Array.isArray(columns)) {
      // POST request with column configuration
      response = await httpClient.post(url, { columns }, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
    } else {
      // GET request with default columns
      response = await httpClient.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
    }
    
    console.log('Response received:', {
      status: response.status,
      contentType: response.headers['content-type'],
      contentLength: response.headers['content-length'],
      disposition: response.headers['content-disposition'],
      dataSize: response.data.byteLength
    });
    
    // Check if response is actually an error (JSON content type)
    if (response.headers['content-type']?.includes('application/json')) {
      const text = new TextDecoder().decode(response.data);
      const errorData = JSON.parse(text);
      throw new Error(errorData.error || 'Unknown error from server');
    }
    
    // Check if response is Excel file
    if (!response.headers['content-type']?.includes('spreadsheetml') && 
        !response.headers['content-type']?.includes('excel') &&
        !response.headers['content-type']?.includes('vnd.openxmlformats')) {
      throw new Error(`Unexpected content type: ${response.headers['content-type']}`);
    }
    
    // Create blob from arraybuffer
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    console.log('Blob created:', { size: blob.size, type: blob.type });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'quotation.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    console.log('Downloading file:', filename);
    
    // Create and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);
    
    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    console.error('Error exporting quotation:', error);
    throw error;
  }
};

// Export quotation with column manager integration
const exportQuotationWithColumns = async (quotationId, selectedColumns) => {
  return exportQuotation(quotationId, selectedColumns);
};

/**
 * Preview product sync changes (NO database modifications)
 * @param {string} quotationId - Quotation MongoDB ObjectId
 * @param {Object} options - Optional sync configuration
 * @param {string[]} options.product_ids - Array of product IDs to preview sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to ['name', 'specification', 'category', 'client_ref_number'])
 * @returns {Promise<Object>} Preview result with changes, affected_count, total_products
 */
const previewSyncQuotationProducts = async (quotationId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/quotations/${quotationId}/preview-sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error previewing quotation product sync:', error);
    throw error;
  }
};

/**
 * Sync product information from products collection to quotation
 * @param {string} quotationId - Quotation MongoDB ObjectId
 * @param {Object} options - Optional sync configuration
 * @param {string[]} options.product_ids - Array of product IDs to sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to ['name', 'specification', 'category', 'client_ref_number'])
 * @returns {Promise<Object>} Sync result with changes
 */
const syncQuotationProducts = async (quotationId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/quotations/${quotationId}/sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error syncing quotation products:', error);
    throw error;
  }
};

const quotationService = {
  getQuotations,
  getQuotation,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  convertQuotationToOrder,
  convertQuotationToOrderPartial,
  getConversionStatus,
  getQuotationStats,
  getClientNames,
  reorderItems,
  exportQuotation,
  exportQuotationWithColumns,
  // Product sync
  previewSyncQuotationProducts,
  syncQuotationProducts,
};

export default quotationService;